import type { ReactNode } from 'react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { SinkId, SourceId } from '../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import type { State } from '../../../Flux/Slice/index.js'
import { useSelector } from '../../../Flux/index.js'
import { exportElementAsPng } from './ExportPng.js'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import { PencilIcon } from '@heroicons/react/24/outline/index.js'
import { LoadingIndicator } from '../../Common/index.ts'
import { createSelector } from '@reduxjs/toolkit'
import { taxaPathKey } from 'Util/Visualisation/TaxaPathKey.ts'
import type { SinkTypeKey } from './Sinks/SinkRegistry.ts'
import { SINK_REGISTRY } from './Sinks/SinkRegistry.ts'
import { alternativeKinds, kindLabel } from './Sinks/ChartTypeSwitch.ts'
import { Profiler } from 'react'
import { recordRender } from '../../../Repository/Visualization/VizPerf.ts'
import type { SinkTable } from './DataTable/SinkTable.ts'
import { csvFilename, downloadCsv } from './DataTable/SinkTable.ts'
import { SinkTablePublisher } from './DataTable/SinkTableContext.tsx'
import { SinkTableView } from './DataTable/SinkTableView.tsx'

type SourceHeader = {
  id: string
  name?: string
  status: string
  kpiActive: boolean
}

const MISSING_SOURCE: SourceHeader = { id: 'missing', name: 'missing', status: 'init', kpiActive: false }

/** ~ms the post-zoom "recomputing" pulse stays on. Long enough to read, short enough to feel snappy. */
const ZOOM_PULSE_MS = 280

/**
 * Generic "taxa is navigating / data is recomputing" feedback. Watches the source's taxa-zoom
 * knobs (selectedPath + gradeThreshold) and returns `true` briefly after any change, so the sink
 * content can dip + settle. Sink-type-agnostic — every sink gets the same cue for free.
 */
function useZoomPulse(sourceId: SourceId): boolean {
  const zoomToken = useSelector((s: typeof State) => {
    const shared = s.visualization.sources[sourceId]?.shared
    return `${taxaPathKey(shared?.selectedPath ?? [])}|${shared?.gradeThreshold ?? 0}`
  })
  const [pulsing, setPulsing] = useState(false)
  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false // don't pulse on initial mount, only on real navigation
      return
    }
    setPulsing(true)
    const t = setTimeout(() => setPulsing(false), ZOOM_PULSE_MS)
    return () => clearTimeout(t)
  }, [zoomToken])
  return pulsing
}

const makeSelectSourceHeader = (sourceId: SourceId) =>
  createSelector([(s: typeof State) => s.visualization.sources[sourceId]], (src): SourceHeader => {
    if (!src) return MISSING_SOURCE
    return { id: src.id, name: src.name, status: src.status, kpiActive: src.shared.natureKPIs !== undefined }
  })

export default function SinkContainer({
  sourceId,
  sinkId,
  title,
  kindKey,
  onRemove,
  onRenamePanel,
  onChangeKind,
  children,
  isHighlighted,
  editing
}: {
  sourceId: SourceId
  sinkId: SinkId
  title: string
  kindKey: SinkTypeKey
  onRemove?: () => void
  onRenamePanel?: (newName: string) => void
  onChangeKind?: (kind: SinkTypeKey) => void
  children: ReactNode
  isHighlighted: boolean
  /** Customize mode: drag, rename, remove and the source badge. Viewing keeps the chart-type switch and the data. */
  editing: boolean
}) {
  const selectSourceHeader = useMemo(() => makeSelectSourceHeader(sourceId), [sourceId])

  const source = useSelector(selectSourceHeader)

  const pulsing = useZoomPulse(sourceId)

  const captureRef = React.useRef<HTMLDivElement>(null)

  const capabilities = SINK_REGISTRY[kindKey].capabilities ?? {}
  const alternatives = alternativeKinds(kindKey)

  const onExport = async () => {
    const node = captureRef.current
    if (!node) return
    await exportElementAsPng(node, `${title || sinkId}.png`, { padding: 8 })
  }

  // The numbers the sink last rendered, if it publishes any. Held here rather than in the sink so
  // the chart/table toggle and the CSV export are the same chrome for every sink type.
  const [table, setTable] = useState<SinkTable | undefined>(undefined)
  const [showTable, setShowTable] = useState(false)

  const onExportCsv = () => {
    if (table) downloadCsv(table, csvFilename(title || sinkId))
  }

  const [nameEdit, setNameEdit] = React.useState<string | null>(null)
  const exportMenuRef = useRef<HTMLDetailsElement>(null)
  const exportAs = (run: () => void) => {
    exportMenuRef.current?.removeAttribute('open')
    run()
  }
  useEffect(() => {
    const closeOnOutsideClick = (e: MouseEvent) => {
      const menu = exportMenuRef.current
      if (menu?.open && !menu.contains(e.target as Node)) menu.removeAttribute('open')
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [])

  const startEdit = () => setNameEdit(title)

  const stopEdit = () => {
    if (nameEdit === null) return
    onRenamePanel?.(nameEdit)
    setNameEdit(null)
  }

  return (
    <div className="card bg-base-100 border shadow-sm h-full flex flex-col" data-testid={`sink-${kindKey}`}>
      {editing && <div className="drag-handle h-2 bg-gray-400 cursor-grab" />}

      <div
        className={`card-title p-2 border-b grow-0 text-sm flex flex-wrap justify-between items-center ${
          isHighlighted ? 'bg-primary' : ''
        }`}
      >
        <div className="flex gap-2 items-center">
          {nameEdit == null && (
            <>
              <TooltipTrigger tooltip={sinkId}>
                <span>{title}</span>
              </TooltipTrigger>
              {editing && (
                <TooltipTrigger tooltip="Edit sink title">
                  <button className="btn btn-xs btn-ghost" onClick={startEdit} aria-label="Rename sink">
                    <PencilIcon className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
              )}
            </>
          )}

          {nameEdit != null && (
            <>
              <input className="input input-xs" value={nameEdit} onChange={(e) => setNameEdit(e.target.value)} />
              <button className="btn btn-xs btn-primary" onClick={stopEdit}>
                Ok
              </button>
            </>
          )}

          {editing && (
            <TooltipTrigger tooltip={source.id}>
              <span className="text-xs opacity-60">[{source.name ?? source.id}]</span>
            </TooltipTrigger>
          )}

          {source.kpiActive && !capabilities.supportsKPI && (
            <TooltipTrigger tooltip={"This sink doesn't support KPIs, data is rendered in taxon mode instead"}>
              <div className={'badge badge-xs badge-warning'}>Taxon Mode</div>
            </TooltipTrigger>
          )}
        </div>

        <div className="flex gap-2">
            {onChangeKind && alternatives.length > 0 && (
              <select
                data-testid="sink-change-kind"
                aria-label="Chart type"
                title="Chart type"
                className="select select-bordered select-xs"
                value={kindKey}
                onChange={(e) => onChangeKind(e.target.value as SinkTypeKey)}
              >
                {[kindKey, ...alternatives].map((kind) => (
                  <option key={kind} value={kind}>
                    {kindLabel(kind)}
                  </option>
                ))}
              </select>
            )}
            {table && (
              <button
                data-testid="sink-toggle-table"
                className={`btn btn-xs ${showTable ? 'btn-active' : ''}`}
                onClick={() => setShowTable((on) => !on)}
                title={showTable ? 'Back to the chart' : 'Show the numbers behind this chart'}
              >
                {showTable ? 'Chart' : 'Numbers'}
              </button>
            )}
            <details ref={exportMenuRef} className="dropdown dropdown-end">
              <summary data-testid="sink-export" className="btn btn-xs">
                Export
              </summary>
              <ul className="dropdown-content menu menu-sm bg-base-100 rounded-box shadow z-20 w-32 p-1">
                <li>
                  <button data-testid="sink-export-png" onClick={() => exportAs(() => void onExport())}>
                    PNG image
                  </button>
                </li>
                {table && (
                  <li>
                    <button data-testid="sink-export-csv" onClick={() => exportAs(onExportCsv)}>
                      CSV numbers
                    </button>
                  </li>
                )}
              </ul>
            </details>
            {editing && onRemove && (
              <button
                data-testid="sink-remove"
                className="btn btn-xs btn-outline btn-error"
                onClick={onRemove}
                title="Remove sink"
              >
                Remove
              </button>
            )}
          </div>
      </div>

      <div
        ref={captureRef}
        data-panel-capture
        className="p-0 flex-1 min-h-0 overflow-hidden bg-white relative"
        style={{
          // Brief dip-and-settle on taxa navigation: signals "recomputing" without a per-sink
          // animation. Transform is cheap/compositor-only and doesn't blur canvas/SVG charts.
          transition: 'opacity 140ms ease, transform 140ms ease',
          opacity: pulsing ? 0.45 : 1,
          transform: pulsing ? 'translateY(3px)' : 'none'
        }}
      >
        {pulsing && (
          <div className="absolute top-1 right-1 z-10 flex items-center gap-1 text-[10px] text-gray-500 opacity-80">
            <span className="loading loading-spinner loading-xs" />
            <span>updating…</span>
          </div>
        )}
        {source.status === 'dataReady' ? (
          // Profiler's actualDuration measures this sink's React commit. It INCLUDES the
          // in-render selector "crunch" (cube.filter + cube.aggregate) — subtract those from the
          // VizPerf report to isolate the paint. No-op unless vizPerf is enabled.
          <Profiler id={`${kindKey}:${sinkId}`} onRender={(id, phase, actual) => recordRender(id, phase, actual)}>
            {/* The sink stays mounted behind the table: it is what computes and publishes the
                numbers, so unmounting it would take the table down with it. Hidden by visibility
                rather than `display`, which would collapse the box and leave the chart sized 0×0
                on the way back. */}
            <div className={showTable && table ? 'absolute inset-0 invisible pointer-events-none' : 'h-full'}>
              <SinkTablePublisher publish={setTable}>{children}</SinkTablePublisher>
            </div>
            {showTable && table && <SinkTableView table={table} />}
          </Profiler>
        ) : (
          <div className={'flex justify-center items-center h-full text-gray-400 text-sm opacity-70 gap-2'}>
            <LoadingIndicator />
          </div>
        )}
      </div>
    </div>
  )
}
